import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Button, CheckboxInput, Loading, Spinner, Typography } from "@/components/atoms";
import { useProfile } from "@/hooks";
import { useUpdateVehicleMutation } from "@/redux/apis/vehicle/vehicleApi";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { addToast } from "@/utils";
import { VehicleDocumentUploadField } from "../../common/VehicleDocumentUploadField";
import { VehicleDocumentDateInput } from "../../EditVehicle/common/VehicleDocumentDateInput";
import { useVehicleDocuments } from "../../common/useVehicleDocuments";
import { useAddVehicleWizardContext } from "../AddVehicleWizardContext";
import { step3Schema, Step3FormData } from "../schemas";

interface Step3VehicleDocumentsProps {
  vehicleId: string;
  vehicle: Vehicle;
  onNext: () => Promise<void>;
  onBack: () => void;
  onFinishLater: () => void;
}

export const Step3VehicleDocuments = ({ vehicleId, vehicle, onNext, onBack, onFinishLater }: Step3VehicleDocumentsProps) => {
  const { dateFormat } = useProfile();
  const { documentRetryCount, setDocumentRetryCount } = useAddVehicleWizardContext();
  const [updateVehicle, { isLoading: isSaving }] = useUpdateVehicleMutation();

  // Initialize from existing vehicle documents (for "Continue" flow)
  const existingInsurance = useMemo(() => vehicle.documents.find((d) => d.documentType === "INSURANCE"), [vehicle.documents]);
  const existingAccreditation = useMemo(() => vehicle.documents.find((d) => d.documentType === "ACCREDITATION"), [vehicle.documents]);

  const [insuranceDocId, setInsuranceDocId] = useState<string | null>(existingInsurance?.uuid ?? null);
  const [accreditationDocId, setAccreditationDocId] = useState<string | null>(existingAccreditation?.uuid ?? null);

  const documents = useVehicleDocuments({
    vehicleId,
    insuranceId: insuranceDocId ?? undefined,
    accreditationId: accreditationDocId ?? undefined,
    documentRetryCount,
    setDocumentRetryCount,
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<Step3FormData>({
    resolver: zodResolver(step3Schema),
    defaultValues: {
      insuranceDocId: existingInsurance?.uuid ?? "",
      insuranceExpiry: existingInsurance?.expiryDate && new Date(existingInsurance.expiryDate) >= today
        ? new Date(existingInsurance.expiryDate)
        : undefined,
      insuranceExpiryConfirm: existingInsurance?.expiryDate ? new Date(existingInsurance.expiryDate) >= today : false,
      insuranceAutoVerified: false,
      accreditationDocId: existingAccreditation?.uuid ?? null,
      accreditationExpiry: existingAccreditation?.expiryDate && new Date(existingAccreditation.expiryDate) >= today
        ? new Date(existingAccreditation.expiryDate)
        : null,
      accreditationExpiryConfirm: existingAccreditation?.expiryDate ? new Date(existingAccreditation.expiryDate) >= today : false,
      accreditationAutoVerified: false,
    },
  });

  const watchedInsuranceExpiry = watch("insuranceExpiry");
  const watchedInsuranceExpiryConfirm = watch("insuranceExpiryConfirm");
  const watchedAccreditationExpiry = watch("accreditationExpiry");
  const watchedAccreditationExpiryConfirm = watch("accreditationExpiryConfirm");

  const suppressInsuranceMessage = !!watchedInsuranceExpiry && !!watchedInsuranceExpiryConfirm;
  const suppressAccreditationMessage = !!watchedAccreditationExpiry && !!watchedAccreditationExpiryConfirm;

  // Sync auto-verified status from polling into the form
  const insuranceAutoVerified = !documents.insurance.manualExpiryDate && !!documents.insurance.document?.expiryDate;
  const accreditationAutoVerified = !documents.accreditation.manualExpiryDate && !!documents.accreditation.document?.expiryDate;

  // Check if the displayed expiry date is expired
  const displayedInsuranceExpiry = watchedInsuranceExpiry;
  const displayedAccreditationExpiry = watchedAccreditationExpiry;
  const isInsuranceExpired = displayedInsuranceExpiry ? displayedInsuranceExpiry < today : false;
  const isAccreditationExpired = displayedAccreditationExpiry ? displayedAccreditationExpiry < today : false;

  useEffect(() => {
    setValue("insuranceAutoVerified", insuranceAutoVerified);
    if (insuranceDocId && insuranceAutoVerified && documents.insurance.document?.expiryDate && documents.insurance.document?.uuid === insuranceDocId) {
      setValue("insuranceExpiry", new Date(documents.insurance.document.expiryDate));
      setValue("insuranceExpiryConfirm", true);
    }
  }, [insuranceDocId, insuranceAutoVerified, documents.insurance.document?.expiryDate, documents.insurance.document?.uuid, setValue]);

  useEffect(() => {
    setValue("accreditationAutoVerified", accreditationAutoVerified);
    if (accreditationDocId && accreditationAutoVerified && documents.accreditation.document?.expiryDate && documents.accreditation.document?.uuid === accreditationDocId) {
      setValue("accreditationExpiry", new Date(documents.accreditation.document.expiryDate));
      setValue("accreditationExpiryConfirm", true);
    }
  }, [accreditationDocId, accreditationAutoVerified, documents.accreditation.document?.expiryDate, documents.accreditation.document?.uuid, setValue]);

  const onSubmit = async (data: Step3FormData) => {
    try {
      const docPayload: Array<{ uuid: string; expiry_date: string | null; document_type: "INSURANCE" | "ACCREDITATION" }> = [];

      if (data.insuranceDocId) {
        const expiryDate = insuranceAutoVerified && documents.insurance.document?.expiryDate
          ? documents.insurance.document.expiryDate
          : data.insuranceExpiry
            ? format(data.insuranceExpiry, "yyyy-MM-dd")
            : null;
        docPayload.push({ uuid: data.insuranceDocId, expiry_date: expiryDate, document_type: "INSURANCE" });
      }

      if (data.accreditationDocId) {
        const expiryDate = accreditationAutoVerified && documents.accreditation.document?.expiryDate
          ? documents.accreditation.document.expiryDate
          : data.accreditationExpiry
            ? format(data.accreditationExpiry, "yyyy-MM-dd")
            : null;
        docPayload.push({ uuid: data.accreditationDocId, expiry_date: expiryDate, document_type: "ACCREDITATION" });
      }

      await updateVehicle({
        id: vehicleId,
        data: {
          identifier: vehicle.identifier,
          capacity_pax: vehicle.capacity.pax,
          capacity_bags: vehicle.capacity.bags,
          maintenance_fee: vehicle.maintenanceFee,
          ...(vehicle.setupStep === 3 && { setup_step: 4 }),
          documents: docPayload,
          categories: vehicle.categories.map((c) => ({ uuid: c.uuid })),
        },
      }).unwrap();

      await onNext();
    } catch (e: unknown) {
      getErrorMessages(e as Parameters<typeof getErrorMessages>[0]).forEach((m) => addToast("danger", m));
    }
  };

  if (documents.isLoading) return <Loading />;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
      {/* Insurance Section */}
      <div className={documents.insurance.document ? "grid grid-cols-2 gap-5" : ""}>
        <div>
          <Typography variant="action" className="text-neutral-dark-gray">Vehicle Insurance Document</Typography>
          <div className="mt-2">
            <VehicleDocumentUploadField
              vehicleId={vehicleId}
              document={documents.insurance.document}
              documentType="INSURANCE"
              retryCount={documentRetryCount.insurance}
              onUpload={(uuid) => {
                setInsuranceDocId(uuid);
                setValue("insuranceDocId", uuid, { shouldValidate: true });
                setDocumentRetryCount((prev) => ({ ...prev, insurance: 0 }));
                setValue("insuranceExpiry", null as unknown as Date, { shouldDirty: true });
                setValue("insuranceExpiryConfirm", false, { shouldDirty: true });
                setValue("insuranceAutoVerified", false, { shouldDirty: true });
              }}
              onRemove={() => {
                setInsuranceDocId(null);
                setValue("insuranceDocId", "", { shouldValidate: true });
                setDocumentRetryCount((prev) => ({ ...prev, insurance: 0 }));
                setValue("insuranceExpiry", null as unknown as Date, { shouldDirty: true });
                setValue("insuranceExpiryConfirm", false, { shouldDirty: true });
                setValue("insuranceAutoVerified", false, { shouldDirty: true });
              }}
              hasError={!!errors.insuranceDocId}
            />
          </div>
          {errors.insuranceDocId?.message && (
            <Typography className="mt-1 text-sm text-danger">{errors.insuranceDocId.message}</Typography>
          )}
          {!suppressInsuranceMessage && documents.insurance.errorMessage && (
            <Typography className="mt-2 text-sm text-danger">{documents.insurance.errorMessage}</Typography>
          )}
        </div>
        <div>
          {documents.insurance.document && (
            <>
              <Typography variant="action" className="text-neutral-dark-gray">Insurance Expiry Date</Typography>
              <Controller
                control={control}
                name="insuranceExpiry"
                render={({ field }) => {
                  return (
                    <VehicleDocumentDateInput
                      className="mt-2 w-full"
                      selected={field.value}
                      onChange={(date) => {
                        field.onChange(date);
                        setValue("insuranceExpiryConfirm", false);
                      }}
                      placeholderText="-- / -- / ----"
                      dateFormat={dateFormat}
                      minDate={new Date()}
                      disabled={documents.insurance.isLoading || !documents.insurance.manualExpiryDate}
                    />
                  );
                }}
              />
              {isInsuranceExpired && (
                <Typography className="mt-1 text-sm text-danger">
                  Insurance document has expired. Please upload a valid document.
                </Typography>
              )}
              {!isInsuranceExpired && errors.insuranceExpiry?.message && (
                <Typography className="mt-1 text-sm text-danger">{errors.insuranceExpiry.message}</Typography>
              )}
              {!isInsuranceExpired && documents.insurance.manualExpiryDate && !documents.insurance.isLoading && (
                <div className="mt-2">
                  <CheckboxInput
                    {...register("insuranceExpiryConfirm")}
                    id="confirm-insurance"
                    label="I confirm that I am legally responsible for the date entered"
                    className="text-neutral-dark-gray [&_.typography]:!text-sm"
                    variant="check"
                  />
                </div>
              )}
              {!isInsuranceExpired && errors.insuranceExpiryConfirm?.message && (
                <Typography className="mt-1 text-sm text-danger">{errors.insuranceExpiryConfirm.message}</Typography>
              )}
            </>
          )}
        </div>
      </div>

      {/* Accreditation Section */}
      <div className={documents.accreditation.document ? "grid grid-cols-2 gap-5" : ""}>
        <div>
          <Typography variant="action" className="text-neutral-dark-gray">Vehicle Accreditation Document (Optional)</Typography>
          <div className="mt-2">
            <VehicleDocumentUploadField
              vehicleId={vehicleId}
              document={documents.accreditation.document}
              documentType="ACCREDITATION"
              retryCount={documentRetryCount.accreditation}
              onUpload={(uuid) => {
                setAccreditationDocId(uuid);
                setValue("accreditationDocId", uuid, { shouldValidate: true });
                setDocumentRetryCount((prev) => ({ ...prev, accreditation: 0 }));
                setValue("accreditationExpiry", null, { shouldDirty: true });
                setValue("accreditationExpiryConfirm", false, { shouldDirty: true });
                setValue("accreditationAutoVerified", false, { shouldDirty: true });
              }}
              onRemove={() => {
                setAccreditationDocId(null);
                setValue("accreditationDocId", null, { shouldValidate: true });
                setDocumentRetryCount((prev) => ({ ...prev, accreditation: 0 }));
                setValue("accreditationExpiry", null, { shouldDirty: true });
                setValue("accreditationExpiryConfirm", false, { shouldDirty: true });
                setValue("accreditationAutoVerified", false, { shouldDirty: true });
              }}
              hasError={false}
            />
          </div>
          {!suppressAccreditationMessage && documents.accreditation.errorMessage && (
            <Typography className="mt-2 text-sm text-danger">{documents.accreditation.errorMessage}</Typography>
          )}
        </div>
        <div>
          {documents.accreditation.document && (
            <>
              <Typography variant="action" className="text-neutral-dark-gray">Accreditation Expiry Date</Typography>
              <Controller
                control={control}
                name="accreditationExpiry"
                render={({ field }) => {
                  return (
                    <VehicleDocumentDateInput
                      className="mt-2 w-full"
                      selected={field.value}
                      onChange={(date) => {
                        field.onChange(date);
                        setValue("accreditationExpiryConfirm", false);
                      }}
                      placeholderText="-- / -- / ----"
                      dateFormat={dateFormat}
                      minDate={new Date()}
                      disabled={documents.accreditation.isLoading || !documents.accreditation.manualExpiryDate}
                    />
                  );
                }}
              />
              {isAccreditationExpired && (
                <Typography className="mt-1 text-sm text-danger">
                  Accreditation document has expired. Please upload a valid document.
                </Typography>
              )}
              {!isAccreditationExpired && errors.accreditationExpiry?.message && (
                <Typography className="mt-1 text-sm text-danger">{errors.accreditationExpiry.message}</Typography>
              )}
              {!isAccreditationExpired && documents.accreditation.manualExpiryDate && !documents.accreditation.isLoading && (
                <div className="mt-2">
                  <CheckboxInput
                    {...register("accreditationExpiryConfirm")}
                    id="confirm-accreditation"
                    label="I confirm that I am legally responsible for the date entered"
                    className="text-neutral-dark-gray [&_.typography]:!text-sm"
                    variant="check"
                  />
                </div>
              )}
              {!isAccreditationExpired && errors.accreditationExpiryConfirm?.message && (
                <Typography className="mt-1 text-sm text-danger">{errors.accreditationExpiryConfirm.message}</Typography>
              )}
            </>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-col-reverse gap-3 xl:flex-row xl:justify-between xl:gap-4">
        <Button type="button" variant="custom" size="xs" className="text-neutral-silver-gray underline xl:w-auto" onClick={onFinishLater}>
          Finish later
        </Button>
        <div className="flex flex-col-reverse gap-3 xl:flex-row xl:gap-4">
          <Button type="button" variant="secondary" size="lg" className="w-full xl:w-auto" startIcon="ArrowLeft" onClick={onBack}>
            Back
          </Button>
          <Button type="submit" variant="primary" size="lg" className="w-full xl:w-auto" endIcon="ArrowRight" disabled={isSaving || documents.isProcessing}>
            {isSaving ? <Spinner /> : "Next"}
          </Button>
        </div>
      </div>
    </form>
  );
};
